/**
 * Central roles (family-from-photo plan, Task B2): a story about a person (grandparents-day is about a grandparent)
 * is not made without that person in the photo. Every family member drawn must come from the photo.
 *
 * IMPORT-FREE: client + worker safe. The master scripts (content/catalogue/scripts) are SERVER-ONLY, so CENTRAL_ROLES
 * is a copy of their `central` roles, kept equal by central-roles.test.ts (parity). GRANDPARENT_WORD /
 * GRANDPARENT_MIN_AGE are duplicated from utils/manga/family-guard.ts (no cross-import; parity test). No regex
 * lookbehind anywhere in this file (Safari < 16.4 cannot parse one).
 */

export type CentralRole = { roleId: string; needs: string; kind: 'grandparent' };

/** Same shape as CastPerson (lib/picture-book/casting.ts); declared here so this module imports nothing. */
export type CentralCastPerson = {
  name: string;
  age?: number | string | null;
  isMainCharacter?: boolean;
  relationship?: string | null;
};

export const CENTRAL_ROLES: Readonly<Record<string, readonly CentralRole[]>> = {
  'grandparents-day': [{ roleId: 'grandparent1', needs: 'a grandparent', kind: 'grandparent' }],
};

export const GRANDPARENT_WORD =
  /\b(?:grand(?:parent|ma|pa|mother|father|dad|ad)|granny|gran|nana|nani|dadi|nan|nonna|nonno|abuela|abuelo|oma|opa|paati|thatha|lola|lolo)s?\b/i;
export const GRANDPARENT_MIN_AGE = 55;

/** Copy of ageInYears (lib/picture-book/casting.ts); kept local so this module imports nothing. */
function ageInYears(age: unknown): number | undefined {
  if (typeof age === 'number') return Number.isFinite(age) && age >= 0 ? age : undefined;
  if (typeof age !== 'string') return undefined;
  const s = age.trim().toLowerCase();
  if (!s) return undefined;
  if (/^(new[\s-]?born|baby|infant)$/.test(s)) return 0;
  const m = /^(\d+(?:\.\d+)?)\s*(y|yr|yrs|year|years|m|mo|mos|month|months|w|wk|wks|week|weeks)?\.?(?:\s*old)?$/.exec(s);
  if (!m) return undefined;
  const n = Number(m[1]);
  const unit = m[2] ?? 'y';
  if (unit.startsWith('w')) return n / 52;
  if (unit.startsWith('m')) return n / 12;
  return n;
}

/** A grandparent word in the relationship (nana, nani, dadi, abuela, ...), or aged 55 or more (same as family-guard). */
export function isGrandparentOfCast(p: CentralCastPerson): boolean {
  const a = ageInYears(p.age);
  if (a !== undefined && a >= GRANDPARENT_MIN_AGE) return true;
  return typeof p.relationship === 'string' && GRANDPARENT_WORD.test(p.relationship);
}

const FILLS: Record<CentralRole['kind'], (p: CentralCastPerson) => boolean> = {
  grandparent: isGrandparentOfCast,
};

/** The template's central roles nobody in the cast can play ([] for no template or a template without one). */
export function unfilledCentralRoles(templateId: string | null | undefined, cast: CentralCastPerson[]): CentralRole[] {
  if (!templateId) return [];
  const roles = CENTRAL_ROLES[templateId];
  if (!roles) return [];
  // The adapt always maps `hero` to the main character, so the main character never plays a central role
  return roles.filter((r) => !cast.some((p) => !p.isMainCharacter && FILLS[r.kind](p)));
}

/** 'Needs a grandparent in your photo' for a template with a central role, else null. */
export function centralNeedsLine(templateId: string | null | undefined): string | null {
  const roles = templateId ? CENTRAL_ROLES[templateId] : undefined;
  if (!roles || !roles.length) return null;
  return `Needs ${roles.map((r) => r.needs).join(' and ')} in your photo`;
}

/** The refusal copy (owner decision 3). `title` comes from the caller (the story's display name). */
export function centralRoleRefusal(title: string, missing: readonly CentralRole[]): string {
  const needs = missing.map((r) => r.needs).join(' and ');
  return `${title} is about ${needs}, so it needs ${needs} in your photo. Use a photo with them, or pick another story.`;
}
